// The touch layer's pure maths (suggestion "drop-in touch layer"; feel recipe §18, from the swinging-hero teardown):
// a floating stick with walk and run gait hysteresis, and a button cluster laid out from data on a thumb arc around
// the bottom-right corner, sized from the screen's short side, inside the safe areas. No DOM: the overlay
// (`@rpg/runtime/dom`) renders what `layoutTouch` returns, and tests check layouts at any viewport.
//
// A button's place is one of: the primary (the big button in the corner); a slot on the arc (slot 0 lowest and
// furthest left, the last slot highest; a fractional slot sits between two, and `ring: 1` puts it on a second ring just
// outside the arc, for contextual buttons); beside another button (a direction in screen degrees, 0 right and 90 up);
// or anchored to a screen corner. Hidden buttons are laid out too, so revealing one never moves the others.

/** Gait thresholds as fractions of the stick radius: dead zone, the walk band's output, run on and off. */
export const TOUCH_STICK = { dead: 0.12, walkMin: 0.2, walkMax: 0.8, runOn: 0.8, runOff: 0.68 } as const;

/** Where a button goes. */
export type TouchPlace =
  | { kind: "primary" }
  | { kind: "arc"; slot: number; ring?: number }
  | { kind: "beside"; of: string; toward: number; gap?: number }
  | { kind: "anchor"; corner: "top-left" | "top-right" | "bottom-left" | "bottom-right"; x: number; y: number };

/** One on-screen button. It writes `control` (a "Touch:<name>" button control) while held. */
export interface TouchButtonSpec {
  id: string;
  control: string;
  size: "primary" | "secondary" | "small";
  place: TouchPlace;
  /** Laid out but not shown until the game reveals it (actions the game doesn't use yet, contextual ones). */
  hidden?: boolean;
}

/** The whole layout as data. */
export interface TouchLayoutSpec {
  /** The floating stick: writes `control` (a vector) and, while the run latch is on, presses `run` (a button). The
   *  stick zone is x < `zone.right`·width and y > `zone.top`·height. */
  stick: { control: string; run?: string; zone: { right: number; top: number } };
  /** Drag to look: writes `control` (pixel deltas) for drags starting at x ≥ `zone.left`·width outside a button. */
  look: { control: string; zone: { left: number } };
  /** The thumb arc, degrees from the corner (0 along the bottom, 90 straight up). Default 14 to 76. */
  arc?: { from: number; to: number };
  buttons: readonly TouchButtonSpec[];
}

/** Safe-area insets, px. */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** A laid-out button: its centre and diameter, px. */
export interface PlacedButton {
  id: string;
  control: string;
  x: number;
  y: number;
  size: number;
  hidden: boolean;
}

/** What `layoutTouch` returns. */
export interface TouchLayout {
  width: number;
  height: number;
  insets: Insets;
  sizes: { primary: number; secondary: number; small: number };
  stick: { restX: number; restY: number; radius: number; zone: Rect };
  look: { zone: Rect };
  buttons: PlacedButton[];
}

/** An axis-aligned rectangle, px. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Lay the spec out for a viewport and its safe-area insets. Throws on a `beside` that names no button or loops. */
export function layoutTouch(spec: TouchLayoutSpec, w: number, h: number, insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 }): TouchLayout {
  const short = Math.min(w, h);
  const sizes = {
    primary: clamp(Math.round(short * 0.22), 72, 88),
    secondary: clamp(Math.round(short * 0.165), 56, 66),
    small: clamp(Math.round(short * 0.12), 44, 52),
  };
  const R = clamp(Math.round(short * 0.15), 50, 66);
  const stick = {
    restX: insets.left + 26 + R,
    restY: h - insets.bottom - 26 - R,
    radius: R,
    zone: { x: 0, y: h * spec.stick.zone.top, w: w * spec.stick.zone.right, h: h * (1 - spec.stick.zone.top) },
  };
  const look = { zone: { x: w * spec.look.zone.left, y: 0, w: w * (1 - spec.look.zone.left), h } };

  // the thumb corner and the arc (feel recipe §18's buttonArc, with the slot count from the data)
  const px = w - insets.right - 10;
  const py = h - insets.bottom - 10;
  const big = sizes.primary;
  const small = sizes.secondary;
  const d = big / 2 + 6;
  const from = spec.arc?.from ?? 14;
  const to = spec.arc?.to ?? 76;
  const slots = Math.max(0, ...spec.buttons.filter((b) => b.place.kind === "arc" && !b.place.ring).map((b) => Math.floor((b.place as { slot: number }).slot) + 1));
  const step = slots > 1 ? (to - from) / (slots - 1) : 0;
  let r2 = d * Math.SQRT2 + big / 2 + 14 + small / 2;
  if (slots > 1) r2 = Math.max(r2, (small + 18) / (2 * Math.sin(rad(step / 2))));

  const placed = new Map<string, PlacedButton>();
  const visiting = new Set<string>();
  const byId = new Map(spec.buttons.map((b) => [b.id, b]));
  const place = (b: TouchButtonSpec): PlacedButton => {
    const done = placed.get(b.id);
    if (done) return done;
    if (visiting.has(b.id)) throw new Error(`touch layout: "${b.id}" is placed beside itself through a loop`);
    visiting.add(b.id);
    const size = sizes[b.size];
    let x: number;
    let y: number;
    const p = b.place;
    if (p.kind === "primary") {
      x = px - d;
      y = py - d;
    } else if (p.kind === "arc") {
      const deg = slots > 1 ? from + p.slot * step : (from + to) / 2;
      const r = r2 + (p.ring ?? 0) * (small + 18);
      x = px - r * Math.cos(rad(deg));
      y = py - r * Math.sin(rad(deg));
    } else if (p.kind === "beside") {
      const ref = byId.get(p.of);
      if (!ref) throw new Error(`touch layout: "${b.id}" is placed beside unknown button "${p.of}"`);
      const o = place(ref);
      const dist = o.size / 2 + (p.gap ?? 12) + size / 2;
      x = o.x + Math.cos(rad(p.toward)) * dist;
      y = o.y - Math.sin(rad(p.toward)) * dist;
    } else {
      const left = p.corner.endsWith("left");
      const top = p.corner.startsWith("top");
      x = left ? insets.left + p.x + size / 2 : w - insets.right - p.x - size / 2;
      y = top ? insets.top + p.y + size / 2 : h - insets.bottom - p.y - size / 2;
    }
    const out = { id: b.id, control: b.control, x, y, size, hidden: b.hidden === true };
    placed.set(b.id, out);
    visiting.delete(b.id);
    return out;
  };
  const buttons = spec.buttons.map(place);
  return { width: w, height: h, insets, sizes, stick, look, buttons };
}

/** One problem `checkTouchLayout` found. */
export interface TouchLayoutIssue {
  kind: "overlap" | "outside" | "small" | "in-stick-zone" | "duplicate-id";
  buttons: string[];
  message: string;
}

/**
 * Check a laid-out cluster: no two buttons closer than `gap` px edge to edge (hidden ones included, since they can
 * be revealed), every button inside the safe area, none smaller than `minSize` px, none over the stick's rest
 * ring (the stick floats, but its resting place must stay clear).
 */
export function checkTouchLayout(l: TouchLayout, o: { gap?: number; minSize?: number } = {}): TouchLayoutIssue[] {
  const gap = o.gap ?? 4;
  const minSize = o.minSize ?? 44;
  const out: TouchLayoutIssue[] = [];
  const seen = new Set<string>();
  for (const b of l.buttons) {
    if (seen.has(b.id)) out.push({ kind: "duplicate-id", buttons: [b.id], message: `button "${b.id}" appears twice` });
    seen.add(b.id);
    const r = b.size / 2;
    if (b.x - r < l.insets.left || b.x + r > l.width - l.insets.right || b.y - r < l.insets.top || b.y + r > l.height - l.insets.bottom)
      out.push({ kind: "outside", buttons: [b.id], message: `button "${b.id}" leaves the safe area` });
    if (b.size < minSize) out.push({ kind: "small", buttons: [b.id], message: `button "${b.id}" is ${b.size} px, under ${minSize}` });
    const s = l.stick;
    if (Math.hypot(b.x - s.restX, b.y - s.restY) < r + s.radius + gap) out.push({ kind: "in-stick-zone", buttons: [b.id], message: `button "${b.id}" covers the stick's rest ring` });
  }
  for (let i = 0; i < l.buttons.length; i++)
    for (let j = i + 1; j < l.buttons.length; j++) {
      const a = l.buttons[i]!;
      const b = l.buttons[j]!;
      const edge = Math.hypot(a.x - b.x, a.y - b.y) - a.size / 2 - b.size / 2;
      if (edge < gap) out.push({ kind: "overlap", buttons: [a.id, b.id], message: `buttons "${a.id}" and "${b.id}" are ${Math.round(edge)} px apart (want ≥ ${gap})` });
    }
  return out;
}

/** A floating stick's state between moves. */
export interface StickState {
  /** The ring's centre, px. */
  cx: number;
  cy: number;
  /** The run latch. */
  run: boolean;
}

/**
 * Move a floating stick's thumb to (fx, fy) with radius R: past the rim the ring slides with the thumb; the output is
 * a direction (y up) with magnitude 0 in the dead zone, 0.2 to 0.8 across the walk band, and 1 while the run latch
 * is on (on at 0.8 of the radius, off below 0.68).
 */
export function stickMove(s: StickState, fx: number, fy: number, R: number): StickState & { x: number; y: number; mag: number } {
  let cx = s.cx;
  let cy = s.cy;
  let dx = fx - cx;
  let dy = fy - cy;
  let dist = Math.hypot(dx, dy);
  if (dist > R) {
    const k = (dist - R) / dist;
    cx += dx * k;
    cy += dy * k;
    dx -= dx * k;
    dy -= dy * k;
    dist = R;
  }
  const r = dist / R;
  const run = r >= (s.run ? TOUCH_STICK.runOff : TOUCH_STICK.runOn);
  const mag =
    r < TOUCH_STICK.dead ? 0 : run ? 1 : TOUCH_STICK.walkMin + (TOUCH_STICK.walkMax - TOUCH_STICK.walkMin) * Math.min(1, (r - TOUCH_STICK.dead) / (TOUCH_STICK.runOn - TOUCH_STICK.dead));
  return { cx, cy, run, mag, x: dist > 0 ? (dx / dist) * mag : 0, y: dist > 0 ? (-dy / dist) * mag : 0 };
}

/** The touch controls a layout writes, by kind (for the contract cross-check). */
export function touchControls(spec: TouchLayoutSpec): { buttons: string[]; vectors: string[]; deltas: string[] } {
  return {
    buttons: [...spec.buttons.map((b) => b.control), ...(spec.stick.run ? [spec.stick.run] : [])],
    vectors: [spec.stick.control],
    deltas: [spec.look.control],
  };
}
